import { Result, ok, err } from 'neverthrow'
import { BuildingId, BuildingDefinition, GameState, Decimal, UpgradeId, BuildingProductionBreakdown, UpgradeContribution } from '../types'
import { buildings, BUILDING_ORDER } from '../data/buildings'
import { upgrades } from '../data/upgrades'

const COST_SCALING_FACTOR = new Decimal(1.15)

export const calculateBuildingCost = (
  building: BuildingDefinition,
  owned: Decimal
): Decimal => {
  return building.baseCost.mul(COST_SCALING_FACTOR.pow(owned))
}

export const getBuildingCount = (
  state: GameState,
  buildingId: BuildingId
): Decimal => {
  return state.buildings.get(buildingId) ?? new Decimal(0)
}

export const canAffordBuilding = (
  state: GameState,
  buildingId: BuildingId
): boolean => {
  const building = buildings.get(buildingId)
  if (building === undefined) {
    return false
  }
  const owned = getBuildingCount(state, buildingId)
  const cost = calculateBuildingCost(building, owned)
  return state.tokens.gte(cost)
}

export const purchaseBuilding = (
  state: GameState,
  buildingId: BuildingId
): Result<GameState, string> => {
  const building = buildings.get(buildingId)
  if (building === undefined) {
    return err(`Unknown building: ${buildingId}`)
  }

  const owned = getBuildingCount(state, buildingId)
  const cost = calculateBuildingCost(building, owned)

  if (state.tokens.lt(cost)) {
    return err(`Cannot afford ${building.name}. Need ${cost}, have ${state.tokens}`)
  }

  const newBuildings = new Map(state.buildings)
  newBuildings.set(buildingId, owned.add(1))

  return ok({
    ...state,
    tokens: state.tokens.sub(cost),
    buildings: newBuildings,
  })
}

export const calculateBaseTPS = (state: GameState): Decimal => {
  let tps = new Decimal(0)

  for (const [buildingId, building] of buildings) {
    const owned = getBuildingCount(state, buildingId)
    tps = tps.add(owned.mul(building.baseProduction))
  }

  return tps
}

export const getTotalBuildingCount = (state: GameState): Decimal => {
  let total = new Decimal(0)
  for (const buildingId of BUILDING_ORDER) {
    total = total.add(getBuildingCount(state, buildingId))
  }
  return total
}

export const calculateBuildingMultiplier = (
  state: GameState,
  buildingId: BuildingId
): Decimal => {
  let multiplier = new Decimal(1)

  for (const upgradeId of state.upgrades) {
    const upgrade = upgrades.get(upgradeId as UpgradeId)
    if (upgrade === undefined) continue

    const effect = upgrade.effect

    if (effect.type === 'doubler' && effect.targetBuilding === buildingId) {
      // Doublers stack multiplicatively
      multiplier = multiplier.mul(2)
    } else if (effect.type === 'synergy' && effect.targetBuilding === buildingId) {
      // Synergy: +bonusPercentage% per source building owned
      let sourceCount: Decimal
      if (effect.sourceBuilding === 'all') {
        sourceCount = getTotalBuildingCount(state)
      } else {
        sourceCount = getBuildingCount(state, effect.sourceBuilding)
      }
      const synergyBonus = sourceCount.mul(effect.bonusPercentage).div(100)
      // Synergies stack multiplicatively: multiply by (1 + bonus)
      multiplier = multiplier.mul(new Decimal(1).add(synergyBonus))
    }
  }

  return multiplier
}

export const calculateTPS = (state: GameState): Decimal => {
  let tps = new Decimal(0)

  for (const [buildingId, building] of buildings) {
    const owned = getBuildingCount(state, buildingId)
    const multiplier = calculateBuildingMultiplier(state, buildingId)
    tps = tps.add(owned.mul(building.baseProduction).mul(multiplier))
  }

  return tps
}

export const calculateBuildingProductionBreakdown = (
  state: GameState,
  buildingId: BuildingId,
  totalTPS: Decimal
): BuildingProductionBreakdown | null => {
  const building = buildings.get(buildingId)
  if (building === undefined) return null

  const quantity = getBuildingCount(state, buildingId)
  const baseTotal = quantity.mul(building.baseProduction)

  const upgradeContributions: UpgradeContribution[] = []
  let runningMultiplier = new Decimal(1)

  for (const upgradeId of state.upgrades) {
    const upgrade = upgrades.get(upgradeId as UpgradeId)
    if (upgrade === undefined) continue

    const effect = upgrade.effect

    if (effect.type === 'doubler' && effect.targetBuilding === buildingId) {
      upgradeContributions.push({
        upgradeId: upgradeId as UpgradeId,
        upgradeName: upgrade.name,
        type: 'doubler',
        multiplierValue: new Decimal(2),
        description: '2x',
      })
      runningMultiplier = runningMultiplier.mul(2)
    } else if (effect.type === 'synergy' && effect.targetBuilding === buildingId) {
      let sourceCount: Decimal
      let sourceName: string
      if (effect.sourceBuilding === 'all') {
        sourceCount = getTotalBuildingCount(state)
        sourceName = 'all buildings'
      } else {
        sourceCount = getBuildingCount(state, effect.sourceBuilding)
        const sourceBuilding = buildings.get(effect.sourceBuilding)
        sourceName = sourceBuilding?.name ?? effect.sourceBuilding
      }
      const bonusPercent = sourceCount.mul(effect.bonusPercentage)
      const multiplierValue = new Decimal(1).add(bonusPercent.div(100))

      upgradeContributions.push({
        upgradeId: upgradeId as UpgradeId,
        upgradeName: upgrade.name,
        type: 'synergy',
        multiplierValue,
        description: `+${bonusPercent.toFixed(0)}% (${sourceCount.toFixed(0)} ${sourceName})`,
      })
      runningMultiplier = runningMultiplier.mul(multiplierValue)
    }
  }

  const finalProduction = baseTotal.mul(runningMultiplier)
  const percentageOfTotalTPS = totalTPS.gt(0)
    ? finalProduction.div(totalTPS).mul(100)
    : new Decimal(0)

  return {
    buildingId,
    buildingName: building.name,
    baseProductionPerUnit: building.baseProduction,
    quantity,
    baseTotal,
    upgradeContributions,
    totalMultiplier: runningMultiplier,
    finalProduction,
    percentageOfTotalTPS,
  }
}

export const applyPassiveGeneration = (
  state: GameState,
  currentTimeMs: number
): GameState => {
  const deltaMs = currentTimeMs - state.lastTickMs
  const deltaSeconds = deltaMs / 1000
  const tps = calculateTPS(state)
  const tokensGenerated = tps.mul(deltaSeconds)

  return {
    ...state,
    tokens: state.tokens.add(tokensGenerated),
    lastTickMs: currentTimeMs,
  }
}
